
import { useState, useRef } from 'react';
import { featuredBars, barMatsAccessories, consumables } from '../../../mocks/equipmentHireCatalogue';

export default function CataloguePdfDownload() {
  const [generating, setGenerating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const pdfContentRef = useRef<HTMLDivElement>(null);

  const handleDownloadPdf = async () => {
    if (generating) return;
    setGenerating(true);

    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas'),
      ]);

      const element = pdfContentRef.current;
      if (!element) return;

      // Temporarily show the hidden content for rendering
      element.style.display = 'block';
      element.style.position = 'absolute';
      element.style.left = '-9999px';
      element.style.top = '0';

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 794,
        windowWidth: 794,
      });

      element.style.display = 'none';
      element.style.position = '';
      element.style.left = '';
      element.style.top = '';

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = position - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save('House-of-FMR-Equipment-Hire-Catalogue.pdf');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      {/* Download Button Section */}
      <section className="py-16 bg-gradient-to-br from-stone-900 via-stone-950 to-stone-900 relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/3 w-80 h-80 bg-amber-400/5 rounded-full blur-3xl"></div>
        <div className="max-w-4xl mx-auto px-6 relative text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/15 border border-amber-400/20 mb-5">
            <i className="ri-file-pdf-2-line text-amber-300 text-sm"></i>
            <span className="text-amber-200 text-xs tracking-widest uppercase font-medium">
              Take It With You
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-white mb-3">
            Download Our Full <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-300">Catalogue</span>
          </h2>
          <p className="text-white/50 text-sm max-w-lg mx-auto mb-8 leading-relaxed">
            Get a beautifully formatted PDF of our complete equipment hire catalogue. Share it with your team, event planner, or keep it for reference.
          </p>
          <button
            onClick={handleDownloadPdf}
            disabled={generating}
            className={`inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap shadow-lg ${
              generating
                ? 'bg-amber-400/50 text-stone-900/50 cursor-wait'
                : 'bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-stone-900 hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 shadow-amber-500/30'
            }`}
          >
            {generating ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Generating PDF...
              </>
            ) : (
              <>
                <i className="ri-download-2-line"></i>
                Download PDF Catalogue
              </>
            )}
          </button>
          {showSuccess && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-400/30">
              <i className="ri-check-line text-emerald-300"></i>
              <span className="text-emerald-200 text-xs font-medium">PDF downloaded successfully!</span>
            </div>
          )}
        </div>
      </section>

      {/* Hidden PDF Content */}
      <div ref={pdfContentRef} style={{ display: 'none', width: '794px', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ padding: '40px', background: '#ffffff' }}>
          {/* PDF Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '3px solid #f59e0b', paddingBottom: '20px', marginBottom: '30px' }}>
            <div>
              <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1c1917', margin: 0 }}>House of FMR</h1>
              <p style={{ fontSize: '11px', color: '#d97706', letterSpacing: '3px', textTransform: 'uppercase', margin: '4px 0 0 0' }}>Equipment Hire Catalogue</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '11px', color: '#78716c', margin: 0 }}>Melbourne, VIC, Australia</p>
              <p style={{ fontSize: '11px', color: '#78716c', margin: '2px 0 0 0' }}>hello@houseoffmr.com</p>
              <p style={{ fontSize: '11px', color: '#78716c', margin: '2px 0 0 0' }}>+61 400 000 000</p>
            </div>
          </div>

          {/* Portable Bars */}
          <div style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <div style={{ width: '4px', height: '24px', background: '#f59e0b', borderRadius: '2px' }}></div>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1c1917', margin: 0 }}>Portable Bars</h2>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#1c1917', color: '#ffffff' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600' }}>Item</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600' }}>Dimensions</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600' }}>Capacity</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600' }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {featuredBars.map((bar, idx) => (
                  <tr key={bar.id} style={{ background: idx % 2 === 0 ? '#fafaf9' : '#ffffff', borderBottom: '1px solid #e7e5e4' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <strong style={{ color: '#1c1917' }}>{bar.name}</strong>
                      <br />
                      <span style={{ color: '#78716c', fontSize: '10px' }}>{bar.description.slice(0, 80)}...</span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#57534e' }}>{bar.dimensions}</td>
                    <td style={{ padding: '10px 12px', color: '#57534e' }}>{bar.capacity}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 'bold', color: '#d97706' }}>${bar.price} <span style={{ fontWeight: 'normal', color: '#a8a29e', fontSize: '10px' }}>{bar.unit}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Bar Mats & Accessories */}
          <div style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <div style={{ width: '4px', height: '24px', background: '#f59e0b', borderRadius: '2px' }}></div>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1c1917', margin: 0 }}>Bar Mats &amp; Accessories</h2>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#1c1917', color: '#ffffff' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600' }}>Item</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600' }}>Dimensions</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600' }}>Min. Qty</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600' }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {barMatsAccessories.map((item, idx) => (
                  <tr key={item.id} style={{ background: idx % 2 === 0 ? '#fafaf9' : '#ffffff', borderBottom: '1px solid #e7e5e4' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <strong style={{ color: '#1c1917' }}>{item.name}</strong>
                      <br />
                      <span style={{ color: '#78716c', fontSize: '10px' }}>{item.description.slice(0, 80)}...</span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#57534e' }}>{item.dimensions}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', color: '#57534e' }}>{item.minQty}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 'bold', color: '#d97706' }}>${item.price} <span style={{ fontWeight: 'normal', color: '#a8a29e', fontSize: '10px' }}>{item.unit}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Consumables */}
          <div style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <div style={{ width: '4px', height: '24px', background: '#f59e0b', borderRadius: '2px' }}></div>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1c1917', margin: 0 }}>Consumables</h2>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#1c1917', color: '#ffffff' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600' }}>Item</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600' }}>Description</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600' }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {consumables.map((item, idx) => (
                  <tr key={item.id} style={{ background: idx % 2 === 0 ? '#fafaf9' : '#ffffff', borderBottom: '1px solid #e7e5e4' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <strong style={{ color: '#1c1917' }}>{item.name}</strong>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#78716c' }}>{item.description.slice(0, 100)}...</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 'bold', color: '#d97706' }}>${item.price.toFixed(2)} <span style={{ fontWeight: 'normal', color: '#a8a29e', fontSize: '10px' }}>{item.unit}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Security Bond Info */}
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '16px', marginBottom: '30px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#92400e', margin: '0 0 8px 0' }}>Security Bond Information</h3>
            <p style={{ fontSize: '11px', color: '#a16207', margin: 0, lineHeight: '1.6' }}>
              A refundable security bond of $100 per equipment category applies to all hire bookings, capped at a maximum of $500.
              The bond is fully refundable upon safe return of all hire goods in their original condition.
            </p>
          </div>

          {/* Terms */}
          <div style={{ borderTop: '2px solid #e7e5e4', paddingTop: '20px', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: 'bold', color: '#1c1917', margin: '0 0 10px 0' }}>Terms &amp; Conditions</h3>
            <ul style={{ fontSize: '10px', color: '#78716c', lineHeight: '1.8', paddingLeft: '16px', margin: 0 }}>
              <li>Payment due within 14 days of invoice date</li>
              <li>50% deposit required to confirm booking</li>
              <li>Delivery &amp; collection within Melbourne metro area</li>
              <li>Equipment must be returned in original condition</li>
              <li>Damage or loss will be charged at replacement value</li>
              <li>Cancellation within 48 hours of event: 100% charge applies</li>
            </ul>
          </div>

          {/* Footer */}
          <div style={{ borderTop: '3px solid #f59e0b', paddingTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ fontSize: '10px', color: '#a8a29e', margin: 0 }}>&copy; 2025 House of FMR Events &amp; Hire. All rights reserved.</p>
            <p style={{ fontSize: '10px', color: '#d97706', margin: 0, fontWeight: '600' }}>houseoffmr.com</p>
          </div>
        </div>
      </div>
    </>
  );
}
